import React, { useState, useEffect } from 'react';
import { useAccessibility } from './AccessibilityContext';
import { contactInfo } from '../config/appConfig';
import { radius, shadows, components } from '../config/designTokens';
import Button from './ui/Button';

// Use centralized configuration
const { whatsapp } = contactInfo;

const Navigation = ({ activeSection, setActiveSection, showAccessibilityPanel, setShowAccessibilityPanel }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { isAccessibilityEnabled } = useAccessibility();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  return (
    <nav 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? `bg-white/95 backdrop-blur-md ${shadows.lg}` : 'bg-white/85 backdrop-blur-md'
      }`}
      role="navigation"
      aria-label="תפריט ניווט ראשי"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <button 
            onClick={() => scrollToSection('hero')}
            className={`flex items-center space-x-3 rtl:space-x-reverse ${components.focus.primary} ${radius.md}`}
            aria-label="חזרה לדף הבית"
          >
            <div className={`w-10 h-10 bg-gradient-to-br from-teal-700 to-teal-600 ${radius.xl} flex items-center justify-center text-white text-xl`}>
              <span role="img" aria-label="לוגו Nice Ice">❄️</span>
            </div>
            <span className="text-xl font-bold text-teal-700">Nice Ice</span>
          </button>
          
          <div className="hidden md:flex items-center space-x-8 rtl:space-x-reverse">
            {/* Accessibility Button */}
            <button
              onClick={() => setShowAccessibilityPanel(true)}
              className={`px-3 py-2 text-sm font-medium ${components.focus.secondary} ${radius.md} flex items-center gap-2 ${
                isAccessibilityEnabled
                  ? 'text-blue-700 bg-blue-50 border border-blue-200'
                  : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
              }`}
              aria-label="פתח הגדרות נגישות"
              title="WCAG 2.1 AA - הגדרות נגישות"
            >
              <span role="img" aria-label="נגישות">♿</span>
              <span className="hidden lg:inline">נגישות</span>
              {isAccessibilityEnabled && (
                <span className={`w-2 h-2 bg-blue-600 ${radius.full}`} aria-label="נגישות פעילה"></span>
              )}
            </button>

            {[
              { id: 'hero', label: 'בית' },
              { id: 'services', label: 'שירותים' },
              { id: 'features', label: 'יתרונות' },
              { id: 'contact', label: 'צור קשר' }
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => scrollToSection(id)}
                className={`px-3 py-2 text-sm font-medium ${components.focus.primary} ${radius.md} ${
                  activeSection === id
                    ? 'text-teal-700 border-b-2 border-teal-700'
                    : 'text-gray-600 hover:text-teal-700'
                }`}
                aria-label={`עבור לקטע ${label}`}
                aria-current={activeSection === id ? 'page' : undefined}
              >
                {label}
              </button>
            ))}
            
            {/* Using our Button component */}
            <Button
              href={`https://wa.me/${whatsapp.phoneNumber}`}
              variant="primary"
              size="md"
              target="_blank"
              rel="noopener noreferrer"
              hrefLang="he-IL"
              aria-label="הזמן עכשיו בווטסאפ"
            >
              הזמן עכשיו
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile Accessibility Button */}
            <button
              onClick={() => setShowAccessibilityPanel(true)}
              className={`p-2 text-sm font-medium ${components.focus.secondary} ${radius.lg} flex items-center gap-1 ${
                isAccessibilityEnabled
                  ? 'text-blue-700 bg-blue-50 border border-blue-200'
                  : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
              }`}
              aria-label="פתח הגדרות נגישות"
              title="WCAG 2.1 AA - הגדרות נגישות"
            >
              <span role="img" aria-label="נגישות">♿</span>
              {isAccessibilityEnabled && (
                <span className={`w-1.5 h-1.5 bg-blue-600 ${radius.full}`} aria-label="נגישות פעילה"></span>
              )}
            </button>

            {/* Using our Button component */}
            <Button
              href={`https://wa.me/${whatsapp.phoneNumber}`}
              variant="primary"
              size="sm"
              target="_blank"
              rel="noopener noreferrer"
              hrefLang="he-IL"
              aria-label="הזמן עכשיו בווטסאפ"
              leftIcon="💬"
            >
              הזמן
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;